import React, { useState } from 'react';
import './CustomizationPanel.css';

const AddTrainerPicker = ({ TRAINER_SPRITES, trainerName, onAdd }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`add-sprite-picker${open ? ' open' : ''}`}>
      <button type="button" className="add-sprite-btn" onClick={() => setOpen(o => !o)}>
        ➕ Add Trainer
      </button>
      <div className="add-sprite-content">
        <div className="add-sprite-content-inner">
          <div className="sprite-thumb-grid">
            {TRAINER_SPRITES.map(t => (
              <button
                key={t}
                type="button"
                className="sprite-thumb-btn"
                onClick={() => { onAdd(t); setOpen(false); }}
                title={trainerName(t)}
              >
                <img src={`/trainers/${t}.png`} alt={trainerName(t)} />
                <span>{trainerName(t)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Shared search + thumbnail grid + custom-name entry used both for adding a
// brand new Pokémon sprite and for swapping an existing sprite's species.
const PokemonThumbPicker = ({ POKEMON_SPRITES, spriteName, onPick }) => {
  const [customName, setCustomName] = useState('');
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();
  const filteredSprites = query
    ? POKEMON_SPRITES.filter(p => spriteName(p).toLowerCase().includes(query))
    : POKEMON_SPRITES;
  return (
    <>
      <input
        className="sprite-search-input"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search Pokémon..."
      />
      <div className="sprite-thumb-grid">
        {filteredSprites.length === 0 && (
          <p className="ctrl-panel-hint sprite-search-empty">No Pokémon match "{search}".</p>
        )}
        {filteredSprites.map(p => (
          <button
            key={p}
            type="button"
            className="sprite-thumb-btn"
            onClick={() => { onPick(p); setSearch(''); }}
            title={spriteName(p)}
          >
            <img className="pixel-sprite" src={`/Pokemons%20individual/${p}.png`} alt={spriteName(p)} />
            <span>{spriteName(p)}</span>
          </button>
        ))}
      </div>
      <div className="custom-pokemon-row">
        <input
          value={customName}
          onChange={e => setCustomName(e.target.value)}
          placeholder="Other Pokémon name..."
        />
        <button
          type="button"
          className="cta-btn-secondary custom-pokemon-add"
          disabled={!customName.trim()}
          onClick={() => { onPick(customName.trim()); setCustomName(''); }}
        >
          Add
        </button>
      </div>
    </>
  );
};

const AddPokemonPicker = ({ POKEMON_SPRITES, spriteName, onAdd }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`add-sprite-picker${open ? ' open' : ''}`}>
      <button type="button" className="add-sprite-btn" onClick={() => setOpen(o => !o)}>
        ➕ Add Pokémon
      </button>
      <div className="add-sprite-content">
        <div className="add-sprite-content-inner">
          <PokemonThumbPicker
            POKEMON_SPRITES={POKEMON_SPRITES}
            spriteName={spriteName}
            onPick={n => { onAdd(n); setOpen(false); }}
          />
        </div>
      </div>
    </div>
  );
};

const LayerRow = ({ sprite, isSelected, isTrainer, onSelect, onDetailsChange, onDelete, onChangeName, POKEMON_SPRITES, spriteName }) => {
  const [changeOpen, setChangeOpen] = useState(false);
  const thumbSrc = isTrainer
    ? `/trainers/${sprite.name}.png`
    : `/Pokemons%20individual/${sprite.name}.png`;
  return (
    <div className={`layer-row-wrap${changeOpen ? ' open' : ''}`}>
      <div className={`layer-row${isSelected ? ' selected' : ''}`} onClick={() => onSelect(sprite.id)}>
        <img
          className={`layer-thumb${isTrainer ? '' : ' pixel-sprite'}`}
          src={thumbSrc}
          alt={sprite.name}
          onError={e => { e.currentTarget.src = '/question_mark3.png'; }}
        />
        <div className="layer-main">
          <div className="layer-name">
            {isTrainer ? sprite.name.replace(/_/g, ' ') : spriteName(sprite.name)}
          </div>
          {isTrainer && (
            <input
              className="layer-details-input"
              value={sprite.details}
              onClick={e => e.stopPropagation()}
              onChange={e => onDetailsChange(sprite.id, e.target.value)}
              placeholder="e.g. Black hair"
            />
          )}
        </div>
        <div className="layer-actions">
          {!isTrainer && (
            <button
              type="button"
              title="Change Pokémon"
              className="layer-change"
              onClick={e => { e.stopPropagation(); setChangeOpen(o => !o); }}
            >
              ✎
            </button>
          )}
          <button type="button" title="Delete" className="layer-delete" onClick={e => { e.stopPropagation(); onDelete(sprite.id); }}>✕</button>
        </div>
      </div>
      {!isTrainer && (
        <div className="layer-change-content">
          <div className="layer-change-content-inner" onClick={e => e.stopPropagation()}>
            <PokemonThumbPicker
              POKEMON_SPRITES={POKEMON_SPRITES}
              spriteName={spriteName}
              onPick={n => { onChangeName(sprite.id, n); setChangeOpen(false); }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const CustomizationPanel = ({
  bg, setBg,
  name, setName,
  hp, setHp,
  bannerText, setBannerText,
  message, setMessage,
  atk1Name, setAtk1Name,
  atk1Dmg,  setAtk1Dmg,
  atk1Desc, setAtk1Desc,
  atk2Name, setAtk2Name,
  atk2Dmg,  setAtk2Dmg,
  sprites, addSprite, updateSprite, deleteSprite, resetSprites,
  selectedId, setSelectedId,
  bgObj,
  BACKGROUNDS, POKEMON_SPRITES, spriteName,
  TRAINER_SPRITES, trainerName,
}) => {
  const sortedSprites = [...sprites].sort((a, b) => b.z - a.z);

  return (
    <div className="controls-column">

      {/* Background */}
      <div className="ctrl-panel">
        <div className="ctrl-panel-header">
          <div className="ctrl-panel-icon">🌄</div>
          <h3>Card Background</h3>
        </div>
        <div className="chip-grid">
          {BACKGROUNDS.map(b => (
            <button
              key={b.id}
              className={`chip ${bg === b.id ? 'selected' : ''}`}
              onClick={() => setBg(b.id)}
            >
              <span className="chip-swatch" style={{ background: b.color }}></span>
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* Trainer & Pokémon sprites */}
      <div className="ctrl-panel">
        <div className="ctrl-panel-header">
          <div className="ctrl-panel-header-left">
            <div className="ctrl-panel-icon">⚡</div>
            <h3>Trainers &amp; Pokémon</h3>
          </div>
          <button type="button" className="reset-sprites-btn" onClick={resetSprites}>
            ↺ Reset to Default
          </button>
        </div>
        <p className="ctrl-panel-hint">Add as many sprites as you like, then drag or flip them directly on the card preview.</p>

        <div className="add-sprite-row">
          <AddTrainerPicker TRAINER_SPRITES={TRAINER_SPRITES} trainerName={trainerName} onAdd={n => addSprite('trainer', n)} />
          <AddPokemonPicker POKEMON_SPRITES={POKEMON_SPRITES} spriteName={spriteName} onAdd={n => addSprite('pokemon', n)} />
        </div>

        {sortedSprites.length > 0 ? (
          <div className="layers-list">
            {sortedSprites.map(s => (
              <LayerRow
                key={s.id}
                sprite={s}
                isSelected={selectedId === s.id}
                isTrainer={s.kind === 'trainer'}
                onSelect={setSelectedId}
                onDetailsChange={(id, v) => updateSprite(id, { details: v })}
                onDelete={deleteSprite}
                onChangeName={(id, n) => updateSprite(id, { name: n })}
                POKEMON_SPRITES={POKEMON_SPRITES}
                spriteName={spriteName}
              />
            ))}
          </div>
        ) : (
          <p className="ctrl-panel-hint">No sprites placed yet — add a trainer or Pokémon above.</p>
        )}
      </div>

      {/* Card Info */}
      <div className="ctrl-panel">
        <div className="ctrl-panel-header">
          <div className="ctrl-panel-icon">✏️</div>
          <h3>Card Info</h3>
        </div>
        <div className="field-row">
          <div className="field">
            <label>Card Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Pikachu"
              maxLength={20}
            />
          </div>
          <div className="field">
            <label>HP</label>
            <input
              type="number"
              value={hp}
              onChange={e => setHp(e.target.value)}
              placeholder="60"
              min={1}
              max={300}
            />
          </div>
        </div>
        <div className="field">
          <label>Gold Banner Text</label>
          <input
            value={bannerText}
            onChange={e => setBannerText(e.target.value)}
            placeholder="e.g. The rarest catch of 'em all"
            maxLength={40}
          />
        </div>
        <div className="field">
          <label>Box Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Add a heartfelt message..."
            maxLength={120}
          />
        </div>
      </div>

      {/* Ability */}
      <div className="ctrl-panel">
        <div className="ctrl-panel-header">
          <div className="ctrl-panel-icon">⚔️</div>
          <h3>Ability</h3>
        </div>
        <div className="field-row">
          <div className="field">
            <label>Ability Name</label>
            <input
              value={atk1Name}
              onChange={e => setAtk1Name(e.target.value)}
              placeholder="I Choose You"
              maxLength={20}
            />
          </div>
          <div className="field">
            <label>Damage</label>
            <input
              type="number"
              value={atk1Dmg}
              onChange={e => setAtk1Dmg(e.target.value)}
              placeholder="20"
              min={0}
              max={300}
            />
          </div>
        </div>
        <div className="field">
          <label>Ability Description</label>
          <textarea
            value={atk1Desc}
            onChange={e => setAtk1Desc(e.target.value)}
            placeholder="Describe the ability effect..."
            maxLength={120}
          />
        </div>
      </div>

    </div>
  );
};

export default CustomizationPanel;
